import { useEffect, useMemo, useState } from "react";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import {
  App,
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  Row,
  Skeleton,
  Typography,
} from "antd";
import {
  AppUpdateSettingKey,
  AppUpdateSettings,
  useGetAppSettingsQuery,
  useUpdateAppSettingsMutation,
} from "api";
import { getErrorMessage } from "components";

interface AppUpdateSettingsCardProps {
  canEdit: boolean;
}

interface AppUpdateFieldDescriptor {
  key: AppUpdateSettingKey;
  label: string;
  help: string;
  example: string;
  kind: "version" | "url";
}

/** Mirrors the server-side rule in AppSettingsService::VERSION_PATTERN. */
const VERSION_PATTERN = /^\d{1,4}(\.\d{1,4}){0,3}$/;

const HTTP_STATUS_UNPROCESSABLE = 422;

const EMPTY_SETTINGS: AppUpdateSettings = {
  "app.min_version.ios": "",
  "app.min_version.android": "",
  "app.latest_version.ios": "",
  "app.latest_version.android": "",
  "app.store_url.ios": "",
  "app.store_url.android": "",
};

const MIN_VERSION_KEYS: AppUpdateSettingKey[] = [
  "app.min_version.ios",
  "app.min_version.android",
];

const PLATFORM_LABELS: Record<AppUpdateSettingKey, string> = {
  "app.min_version.ios": "iOS",
  "app.min_version.android": "Android",
  "app.latest_version.ios": "iOS",
  "app.latest_version.android": "Android",
  "app.store_url.ios": "iOS",
  "app.store_url.android": "Android",
};

const FIELDS: AppUpdateFieldDescriptor[] = [
  {
    key: "app.min_version.ios",
    label: "Versiune minima iOS",
    help: "Utilizatorii cu o versiune mai veche sunt blocati in aplicatie pana actualizeaza. Gol = blocare dezactivata.",
    example: "1.2.0",
    kind: "version",
  },
  {
    key: "app.min_version.android",
    label: "Versiune minima Android",
    help: "Utilizatorii cu o versiune mai veche sunt blocati in aplicatie pana actualizeaza. Gol = blocare dezactivata.",
    example: "1.2.0",
    kind: "version",
  },
  {
    key: "app.latest_version.ios",
    label: "Ultima versiune iOS",
    help: 'Utilizatorii sub aceasta versiune primesc o sugestie de actualizare, cu buton "Mai tarziu". Gol = sugestie dezactivata.',
    example: "1.3.0",
    kind: "version",
  },
  {
    key: "app.latest_version.android",
    label: "Ultima versiune Android",
    help: 'Utilizatorii sub aceasta versiune primesc o sugestie de actualizare, cu buton "Mai tarziu". Gol = sugestie dezactivata.',
    example: "1.3.0",
    kind: "version",
  },
  {
    key: "app.store_url.ios",
    label: "Link App Store",
    help: 'Unde duce butonul "Actualizeaza acum" pe iOS. Gol = aplicatia foloseste link-ul implicit.',
    example: "https://apps.apple.com/app/id6755063245",
    kind: "url",
  },
  {
    key: "app.store_url.android",
    label: "Link Google Play",
    help: 'Unde duce butonul "Actualizeaza acum" pe Android. Gol = aplicatia foloseste link-ul implicit.',
    example: "https://play.google.com/store/apps/details?id=com.astechsoft.spor",
    kind: "url",
  },
];

const FIELD_KEYS = FIELDS.map((field) => field.key);

const validateVersion = (_: unknown, value?: string): Promise<void> => {
  const trimmed = (value ?? "").trim();

  if (trimmed === "" || VERSION_PATTERN.test(trimmed)) {
    return Promise.resolve();
  }

  return Promise.reject(
    new Error("Foloseste 1-4 segmente numerice separate prin punct (ex. 1.2.0).")
  );
};

const validateStoreUrl = (_: unknown, value?: string): Promise<void> => {
  const trimmed = (value ?? "").trim();

  if (trimmed === "") {
    return Promise.resolve();
  }

  try {
    if (new URL(trimmed).protocol === "https:") {
      return Promise.resolve();
    }
  } catch {
    // Not parseable as a URL — same outcome as a non-https one.
  }

  return Promise.reject(
    new Error("Adresa trebuie sa fie un URL valid care incepe cu https://.")
  );
};

/**
 * Lifts the per-field messages out of the server's 422 so each input
 * shows its own error, instead of one opaque notification for six
 * fields. Anything that isn't the expected shape falls back to the
 * generic notification.
 */
const extractFieldErrors = (
  error: FetchBaseQueryError | SerializedError
): { name: AppUpdateSettingKey; errors: string[] }[] => {
  if (!("status" in error) || error.status !== HTTP_STATUS_UNPROCESSABLE) {
    return [];
  }

  const payload = error.data;
  if (typeof payload !== "object" || payload === null) {
    return [];
  }

  const fieldErrors = (payload as { errors?: unknown }).errors;
  if (typeof fieldErrors !== "object" || fieldErrors === null) {
    return [];
  }

  const messagesByKey = fieldErrors as Record<string, unknown>;

  return FIELD_KEYS.filter((key) => key in messagesByKey).map((key) => {
    const messages = messagesByKey[key];
    return {
      name: key,
      errors: Array.isArray(messages) ? messages.map(String) : [String(messages)],
    };
  });
};

/**
 * Server-driven app-update gate.
 *
 * The single most destructive control in the dashboard: a non-empty
 * minimum version locks every user below it out of the app until they
 * update from the store. It is therefore never one click away — saving
 * always goes through a confirmation, and that confirmation spells out
 * who gets blocked whenever a minimum is set.
 */
export const AppUpdateSettingsCard = ({
  canEdit,
}: AppUpdateSettingsCardProps) => {
  const { modal, notification } = App.useApp();
  const [form] = Form.useForm<AppUpdateSettings>();

  const { data, isLoading } = useGetAppSettingsQuery();
  const [updateAppSettings, { isLoading: isSaving, error, data: savedData }] =
    useUpdateAppSettingsMutation();

  // Last values known to be persisted. The draft is compared against
  // this, so the save button re-disables itself the moment a change is
  // reverted by hand.
  const [savedSettings, setSavedSettings] =
    useState<AppUpdateSettings>(EMPTY_SETTINGS);
  const [isValid, setIsValid] = useState(true);

  const watchedValues = Form.useWatch([], form);

  const draft = useMemo<AppUpdateSettings>(() => {
    const values = { ...EMPTY_SETTINGS, ...watchedValues };

    return FIELD_KEYS.reduce<AppUpdateSettings>(
      (trimmed, key) => ({ ...trimmed, [key]: (values[key] ?? "").trim() }),
      EMPTY_SETTINGS
    );
  }, [watchedValues]);

  const isDirty = useMemo(
    () => FIELD_KEYS.some((key) => draft[key] !== savedSettings[key]),
    [draft, savedSettings]
  );

  useEffect(() => {
    const settings = savedData?.settings ?? data?.settings;
    if (settings) {
      setSavedSettings(settings);
      form.setFieldsValue(settings);
    }
  }, [data, savedData, form]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsValid(true))
      .catch(() => setIsValid(false));
  }, [form, watchedValues]);

  useEffect(() => {
    if (!error) {
      return;
    }

    const fieldErrors = extractFieldErrors(error);
    if (fieldErrors.length > 0) {
      form.setFields(fieldErrors);
    }

    notification.error({
      message:
        fieldErrors.length > 0
          ? "Verifica valorile marcate — nimic nu a fost salvat."
          : getErrorMessage(error),
    });
  }, [error, form, notification]);

  const confirmAndSave = () => {
    const blockingKeys = MIN_VERSION_KEYS.filter((key) => draft[key] !== "");

    modal.confirm({
      title: "Salvezi setarile de actualizare?",
      okText: "Salveaza",
      cancelText: "Renunta",
      okButtonProps: { danger: blockingKeys.length > 0 },
      width: 520,
      content:
        blockingKeys.length > 0 ? (
          <div>
            <Typography.Paragraph strong type="danger">
              ATENTIE: setezi o versiune minima. Toti utilizatorii care ruleaza
              o versiune mai veche vor fi BLOCATI in aplicatie, cu un ecran fara
              buton de inchidere, pana cand actualizeaza din magazin.
            </Typography.Paragraph>
            <ul>
              {blockingKeys.map((key) => (
                <li key={key}>
                  <Typography.Text code>
                    {PLATFORM_LABELS[key]} &lt; {draft[key]}
                  </Typography.Text>
                </li>
              ))}
            </ul>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Modificarea se aplica la urmatoarea pornire a aplicatiei, fara
              build nou si fara publicare in magazin.
            </Typography.Paragraph>
          </div>
        ) : (
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            Nicio versiune minima nu este setata, deci niciun utilizator nu va
            fi blocat. Modificarea se aplica la urmatoarea pornire a aplicatiei.
          </Typography.Paragraph>
        ),
      onOk: () =>
        updateAppSettings(draft)
          .unwrap()
          .then(() => {
            notification.success({
              message: "Setarile de actualizare au fost salvate",
            });
          })
          // The error effect above renders the failure; swallowing the
          // rejection here only stops antd logging an unhandled one.
          .catch(() => undefined),
    });
  };

  if (isLoading) {
    return (
      <Card title="Actualizare aplicatie" style={{ marginTop: 16 }}>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    );
  }

  return (
    <Card title="Actualizare aplicatie" style={{ marginTop: 16 }}>
      <Typography.Paragraph type="secondary">
        Magazinele nu expun un API sigur pentru "ce versiune este live", asa ca
        serverul este sursa de adevar: aplicatia isi compara la pornire versiunea
        instalata cu valorile de mai jos. Un camp gol dezactiveaza complet nivelul
        respectiv.
      </Typography.Paragraph>

      <Form<AppUpdateSettings>
        form={form}
        layout="vertical"
        disabled={!canEdit}
        initialValues={EMPTY_SETTINGS}
      >
        <Row gutter={[16, 0]}>
          {FIELDS.map((field) => (
            <Col xs={24} md={12} key={field.key}>
              <Form.Item
                name={field.key}
                label={field.label}
                validateTrigger={["onChange", "onBlur"]}
                rules={[
                  {
                    validator:
                      field.kind === "version"
                        ? validateVersion
                        : validateStoreUrl,
                  },
                ]}
                extra={
                  <span>
                    {field.help}{" "}
                    <Typography.Text code>{field.key}</Typography.Text>{" "}
                    <Typography.Text code>ex. {field.example}</Typography.Text>
                  </span>
                }
              >
                <Input
                  allowClear
                  placeholder={field.example}
                  autoComplete="off"
                />
              </Form.Item>
            </Col>
          ))}
        </Row>
      </Form>

      <Flex justify="flex-end">
        <Button
          type="primary"
          danger={MIN_VERSION_KEYS.some((key) => draft[key] !== "")}
          loading={isSaving}
          disabled={!canEdit || !isDirty || !isValid}
          onClick={confirmAndSave}
        >
          Salveaza setarile de actualizare
        </Button>
      </Flex>
    </Card>
  );
};
